import React from 'react';
import Icon from '../../../components/AppIcon';

const ReportPreview = ({ data, reportType, filters, loading }) => {
  if (loading) {
    return (
      <div className="bg-card rounded-lg border border-border p-12">
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="animate-spin">
            <Icon name="Loader2" size={48} className="text-primary" />
          </div>
          <p className="text-muted-foreground">Loading report data...</p>
        </div>
      </div>
    );
  }

  if (data?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-12">
        <div className="flex flex-col items-center justify-center space-y-4">
          <Icon name="FileX" size={48} className="text-muted-foreground" />
          <h3 className="text-xl font-bold text-foreground">No Data Found</h3>
          <p className="text-muted-foreground text-center">
            No records match your current filters. Try adjusting your filter criteria.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border">
      {/* Header */}
      <div className="p-6 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-foreground capitalize">{reportType} Report</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {data?.length} record{data?.length !== 1 ? 's' : ''} found
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Icon name="Calendar" size={16} />
            <span>{new Date()?.toLocaleDateString()}</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted/50">
            <tr>
              {reportType === 'players' && (
                <>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Position</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Team</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Goals</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Assists</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Appearances</th>
                </>
              )}
              {reportType === 'coaches' && (
                <>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">License</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Club</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Games</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Wins</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Win Rate</th>
                </>
              )}
              {reportType === 'goalkeepers' && (
                <>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Team</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Appearances</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Clean Sheets</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Goals Conceded</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">Saves</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {reportType === 'players' && data?.map((player, index) => (
              <tr key={index} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">{player?.fullName || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground capitalize">{player?.position || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{player?.currentTeam || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{player?.goalsScored || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{player?.assists || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{player?.totalAppearances || 0}</td>
              </tr>
            ))}
            {reportType === 'coaches' && data?.map((coach, index) => (
              <tr key={index} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">{coach?.fullName || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground uppercase">{coach?.coachingLicense || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{coach?.presentClub || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{coach?.totalGamesManaged || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{coach?.gamesWon || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                  {coach?.gamesWon && coach?.totalGamesManaged ? `${((coach?.gamesWon / coach?.totalGamesManaged) * 100)?.toFixed(1)}%` : '0%'}
                </td>
              </tr>
            ))}
            {reportType === 'goalkeepers' && data?.map((gk, index) => (
              <tr key={index} className="hover:bg-muted/30 transition-colors">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">{gk?.fullName || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{gk?.currentTeam || 'N/A'}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{gk?.totalAppearances || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{gk?.cleanSheets || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{gk?.goalsConceded || 0}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">{gk?.savesMade || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ReportPreview;